import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { useFormikContext } from 'formik';

import useSession from 'core/hooks/use-session';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { Select } from 'core/components/select';
import { Tooltip } from 'core/components/tooltip';
import { buildSweepPaymentMethodOptions } from './sweep-bind-option.helpers';
import { useStore } from 'core/store';
import { isQuoteStepRailEnabled } from 'core/helpers/quote-revisit';
import useSweepAccount from './use-sweep-account';
import useStyles from './sweep-bind-option.styles';

/**
 * New-business bind option (#33155): lets an independent agent pay the initial (down) payment
 * from their agency's sweep account. Shown only when the agent's affinity stem matches the
 * offer's and their agency has a sweep account. The customer's own payment method (collected in
 * the billing section above) is still saved for future installments — sweep covers the down
 * payment only. Eligible agents with no sweep account see a link to set one up.
 */
const SweepBindOption = ({ offer }) => {
  const classes = useStyles();
  const { canManageSweepAccount } = useSession();
  const { values, setFieldValue } = useFormikContext();

  const { eligible, loading, sweepAccount } = useSweepAccount(offer);
  const { offer: store } = useStore();
  const appliedChoice = useRef(false);
  useEffect(() => {
    if (appliedChoice.current || !sweepAccount) {
      return;
    }
    appliedChoice.current = true;
    if (isQuoteStepRailEnabled() && store.quotePurchaseOfferId === offer.id && store.quotePurchaseChoice === 'sweep') {
      setFieldValue('useSweepAccount', true);
      setFieldValue('sweepPaymentMethodId', sweepAccount.defaultPaymentMethodId || sweepAccount.paymentMethods[0]?.id);
    }
  }, [sweepAccount, store, offer.id, setFieldValue]);

  // Render nothing until the account query resolves — otherwise the "set up a sweep account"
  // hint briefly flashes before an existing account loads.
  if (!eligible || loading) {
    return null;
  }

  // Only someone who can actually set the account up gets the setup hint; a producer whose
  // agency has no sweep account sees nothing. (#32606)
  if (!sweepAccount) {
    if (!canManageSweepAccount) {
      return null;
    }
    return (
      <Card className={classes.container}>
        <Tooltip
          label="sweep account payments"
          text="Set up a sweep account in the account menu (top-right person icon)."
          className={classes.hint}
        />
      </Card>
    );
  }

  const onToggle = (event) => {
    const { checked } = event.target;
    setFieldValue('useSweepAccount', checked);
    if (checked && !values.sweepPaymentMethodId) {
      setFieldValue('sweepPaymentMethodId', sweepAccount.defaultPaymentMethodId || sweepAccount.paymentMethods[0]?.id);
    }
  };

  return (
    <Card className={classes.container}>
      <FormControlLabel
        control={<Checkbox checked={Boolean(values.useSweepAccount)} onChange={onToggle} color="primary" />}
        label="Pay the initial payment from your agency sweep account"
      />
      {values.useSweepAccount && (
        <div className={classes.methodRow}>
          <Label type="infoLabel">Sweep payment method</Label>
          <Select
            fullWidth
            mode="light"
            name="sweepPaymentMethodId"
            value={values.sweepPaymentMethodId || ''}
            options={buildSweepPaymentMethodOptions(sweepAccount.paymentMethods)}
            onChange={(_name, value) => setFieldValue('sweepPaymentMethodId', value)}
            data-cy="sweep-bind-payment-method"
          />
          <Label type="infoValue" className={classes.note}>
            The customer&apos;s own payment method above is still saved for future installments.
          </Label>
        </div>
      )}
    </Card>
  );
};

SweepBindOption.propTypes = {
  offer: PropTypes.object.isRequired
};

export default SweepBindOption;
